import { assertNever } from '@kofno/piper';
import { Maybe } from 'maybeasy';
import { useEffect, useRef, useState } from 'react';
import { Link } from '../../../../../../Resource/Types';
import { StringOption } from '../../../../../Options/Types';
import { setFilteringState, setOptionSelectedState } from './Actions';
import { getPlaceholder, isLoading, options } from './Computeds';
import { handleInputChange, handleSelectionChange } from './Handlers';
import { fetchOptionsReaction, loadingReaction } from './Reactions';
import { loading, State } from './Types';

export const useOrganizationFilterTypeahead = (
  endpoint: Link,
  onChange: (value: StringOption) => void,
  initialValue: Maybe<StringOption>,
) => {
  const [state, setState] = useState<State>(loading(initialValue));

  const debouncer = useRef<NodeJS.Timeout | undefined>(undefined);

  // Watch for changes to initialValue and update state accordingly (adjust during render)
  const [prevInitialValue, setPrevInitialValue] = useState(initialValue);
  if (initialValue !== prevInitialValue) {
    setPrevInitialValue(initialValue);
    // Only reset the state if we're not in the middle of filtering or loading
    if (state.kind !== 'filtering' && state.kind !== 'loading') {
      setState(loading(initialValue));
    }
  }

  useEffect(() => {
    switch (state.kind) {
      case 'loading':
        loadingReaction(endpoint, state, setState, initialValue);
        break;
      case 'filtering':
        fetchOptionsReaction(endpoint, state, setState);
        break;
      case 'error':
      case 'filtered':
      case 'option-selected':
      case 'ready':
        break;
      default:
        assertNever(state);
    }
  }, [state, endpoint, initialValue]);

  return {
    state,
    handleInputChange: handleInputChange(debouncer, setFilteringState(state, setState)),
    handleSelectionChange: handleSelectionChange(onChange, setOptionSelectedState(state, setState)),
    isLoading: isLoading(state),
    options: options(state),
    placeholder: getPlaceholder(state),
  };
};
